// KPI Builder — ready-made charts the backend already draws. Shapes of the live “Course Dropout Rate”, “Reattempt Rate”,
// “Avg Attempts to Pass”, “Facilities with High Non-Completion Rate” and compared “Time-on-Task Distribution” KPIs.
import { KPI_FILTERS } from 'constants/kpiBuilderCatalogue';
import { envelope, filterFor, RecipeContext, RecipeResult, snake, traitClause } from './queries';
import { isFilterReady } from './custom';
import { registeredAndCompleted } from './noCompletionRate';
import { groupOf } from './lookups';
import { QUALITY_DATES } from './progressRecipes';
import { isRangeReady, rangeBuckets } from './rangeRecipe';
import { BuilderAnswers, EsNode, QueryObject, RangeBucket, ReadyKind, SourceId } from './types';

type EsClause = Record<string, unknown>;

export interface ReadyKindInfo {
  label: string;
  desc: string;
  title: string;
  summary: string;
  /** Chart the dashboard draws it with. */
  chart: string;
  src: SourceId;
  srcB: SourceId | null;
  handler: string;
}

export const READY_KINDS: Record<ReadyKind, ReadyKindInfo> = {
  dropout: {
    label: 'Dropout rate', title: 'Dropout rate', src: 'enr', srcB: null, handler: 'dropoutrate', chart: 'horizontalbar',
    desc: 'Answers: which groups lose the most learners after they enrol. You get: bars of the % of enrolments not completed, highest first.',
    summary: 'For each group, shows the % of enrolments that were not completed, highest first',
  },
  reattempt: {
    label: 'Reattempt rate', title: 'Reattempt rate', src: 'assess', srcB: null, handler: 'reattemptrate', chart: 'donut',
    desc: 'Answers: how often learners retry a course assessment. You get: a donut of the share of learner and course pairs attempted more than once.',
    summary: 'Shows the share of learners who attempted a course assessment more than once',
  },
  attempts: {
    label: 'Average attempts to pass', title: 'Average attempts to pass by competency', src: 'assess', srcB: null, handler: 'attemptstopassbycompetency', chart: 'horizontalbar',
    desc: 'Answers: which competencies take learners the most tries to pass. You get: one bar per competency showing the average number of attempts needed.',
    summary: 'For each competency, shows how many attempts learners needed on average to pass',
  },
  facility: {
    label: 'Facilities with high non-completion', title: 'Facilities with high non-completion rate', src: 'reg', srcB: 'enr', handler: 'facilitynoncomplete', chart: 'horizontalbar',
    desc: 'Answers: which facilities have the most registered learners who have not completed. You get: bars of the non-completion % per facility, highest first, with facilities below your limit left out.',
    summary: 'For each facility, shows the % of registered learners who have not completed, highest first',
  },
  levelFunnel: {
    label: 'Level-wise funnel', title: 'Level-wise funnel', src: 'enr', srcB: 'pass', handler: 'competencyenrollmentfunnel', chart: 'funnel',
    desc: 'Answers: how many learners who enrol in a competency go on to reach each level. You get: a funnel from enrolled learners down to Level 1, 2, 3, 4 and 5, for one competency at a time with the dashboard’s Competency filter, or for all of them together.',
    summary: 'Shows a funnel from enrolled learners down to each competency level, Level 1 to 5, for one competency at a time or all together',
  },
  completedFunnel: {
    label: 'Completed-learners level funnel', title: 'Completed-learners level funnel', src: 'enr', srcB: 'pass', handler: 'competencyenrollmentfunnel', chart: 'funnel',
    desc: 'Answers: of the learners who completed the course, how many reached each level. You get: the level-wise funnel with learners who completed as the first step, then Level 1 to 5. Levels come from the passbook, so they are not limited to completers.',
    summary: 'Shows a funnel from learners who completed the course down to each competency level, Level 1 to 5',
  },
  histogram: {
    label: 'Two distributions side by side', title: 'Distribution compared', src: 'activity', srcB: null, handler: 'histogramcomparison', chart: 'multibar',
    desc: 'Answers: how a number, such as time spent, is spread for two kinds of records, for example courses and assessments. You get: two sets of bars side by side across the ranges you define.',
    summary: 'Shows how a number spreads across ranges for two groups of records, side by side',
  },
};

/** Time spent, in milliseconds, in the ranges the live Time-on-Task KPIs use. */
export const LIVE_TIME_RANGES: RangeBucket[] = [
  ['0-1 min', '0', '60000'], ['1-2 min', '60000', '120000'], ['2-5 min', '120000', '300000'], ['5-10 min', '300000', '600000'],
  ['10-20 min', '600000', '1200000'], ['20-30 min', '1200000', '1800000'], ['30-60 min', '1800000', '3600000'], ['60+ min', '3600000', ''],
].map(([label, from, to]) => ({ label, from, to }));

export const readyTitle = (a: BuilderAnswers): string => {
  if (a.readyKind !== 'dropout') return READY_KINDS[a.readyKind].title;
  return `Dropout rate by ${(groupOf(a)?.label ?? 'competency').toLowerCase()}`;
};

export function isReadyComplete(a: BuilderAnswers): boolean {
  if (a.readyKind === 'dropout') return !!groupOf(a);
  if (a.readyKind !== 'histogram') return true;
  const traitReady = a.rateSeg === 'custom' && isFilterReady(a.custom.rateWhen);
  return isRangeReady(a.custom.rangeField, a.custom.ranges) && traitReady;
}

const terms = (field: string, size: number): EsNode => ({ terms: { field, size } });
/** The clauses a filter is made of, so more can be added next to them. */
const clausesOf = (f: EsClause): EsClause[] => (f as { bool?: { filter?: EsClause[] } }).bool?.filter ?? [];
const PAIR: EsNode = { cardinality: { script: { source: "doc['userId'].value + '_' + doc['courseId'].value", lang: 'painless' } } };

// shape of dropoutrate
function buildDropout(c: RecipeContext): RecipeResult {
  const g = groupOf(c.a);
  const name = snake(g?.id ?? 'competency');
  const field = g?.id === 'competency' ? 'competencyId' : g?.field ?? 'competencyId';
  const size = c.a.topN;
  const bars: EsNode = {
    terms: { field, size, order: { _count: 'desc' } },
    aggs: {
      not_completed: { filter: { terms: { enrollStatus: ['0', '1'] } } },
      dropout_rate: { bucket_script: { buckets_path: { total: '_count', dropped: 'not_completed._count' }, script: 'params.total > 0 ? Math.round(params.dropped * 100.0 / params.total) : 0' } },
      sort_by_rate: { bucket_sort: { sort: [{ dropout_rate: { order: 'desc' } }], size } },
    },
  };
  const path = `aggregations.filtered_data.${name}.buckets`;
  return {
    queries: [{ ...envelope(c.srcId, c.a), filterPath: `${path}.key,${path}.dropout_rate`, aggrQuery: { size: 0, aggs: { filtered_data: { filter: filterFor(c.srcId, c.a), aggs: { [name]: bars } } } } }],
    cfg: { chartType: 'dropoutrate', isDecimal: false, aggregationPaths: ['filtered_data', name] },
    handlerKey: 'dropoutrate',
  };
}

const withCompetency = (q: Omit<QueryObject, 'aggrQuery'>): Omit<QueryObject, 'aggrQuery'> => ({ ...q, requestQueryMap: { ...q.requestQueryMap, Competency: 'competencyId' } });

// shape of reattemptrate
function buildReattempt(c: RecipeContext): RecipeResult {
  const aggs = { filtered_data: { filter: filterFor(c.srcId, c.a), aggs: { all_pairs: PAIR, reattempted: { filter: { range: { attemptNumber: { gt: 1 } } }, aggs: { reattempted_pairs: PAIR } } } } };
  return {
    queries: [{ ...withCompetency(envelope(c.srcId, c.a)), filterPath: 'aggregations.filtered_data', aggrQuery: { size: 0, aggs } }],
    cfg: { chartType: 'reattemptrate', isDecimal: true, aggregationPaths: ['filtered_data'] },
    handlerKey: 'reattemptrate',
  };
}

// shape of attemptstopassbycompetency
function buildAttempts(c: RecipeContext): RecipeResult {
  const base = filterFor(c.srcId, c.a) as { bool?: { filter?: EsClause[]; must_not?: EsClause[] } };
  const filter = { bool: { filter: clausesOf(base), must_not: [...(base.bool?.must_not ?? []), { term: { 'competencyName.keyword': '' } }] } };
  const perCompetency: EsNode = {
    ...terms('competencyName.keyword', c.a.topN),
    aggs: {
      passed: {
        filter: { term: { isPass: true } },
        aggs: { by_user_assessment: { ...terms('userAssessmentKey', 10000), aggs: { min_attempt: { min: { field: 'attemptNumber' } } } }, avg_attempts: { avg_bucket: { buckets_path: 'by_user_assessment>min_attempt' } } },
      },
    },
  };
  const path = 'aggregations.filtered_data.by_competency.buckets';
  return {
    queries: [{ ...withCompetency(envelope(c.srcId, c.a)), filterPath: `${path}.key,${path}.passed.avg_attempts`, aggrQuery: { size: 0, aggs: { filtered_data: { filter, aggs: { by_competency: perCompetency } } } } }],
    cfg: { chartType: 'attemptstopassbycompetency', isDecimal: true, aggregationPaths: ['filtered_data'] },
    handlerKey: 'attemptstopassbycompetency',
  };
}

// shape of facilitynoncomplete: registered per facility, and completed per facility
function buildFacility(c: RecipeContext): RecipeResult {
  const { queries, cfg, handlerKey } = registeredAndCompleted(c, 'facilityName', c.srcId, c.srcBId);
  return { queries, cfg: { ...cfg, chartType: 'facilitynoncomplete', minThreshold: c.a.threshold }, handlerKey };
}

// shape of histogramcomparison: the same ranges counted for two groups of records
function buildHistogram(c: RecipeContext): RecipeResult {
  const { a } = c;
  const ranges = rangeBuckets(a.custom.ranges);
  const base = filterFor(c.srcId, a);
  const side = (suffix: string, filter: EsClause): QueryObject => ({
    ...envelope(c.srcId, a),
    requestQueryMap: { ...envelope(c.srcId, a).requestQueryMap, competencyId: 'competencyId' },
    filterPath: `aggregations.filtered_data_${suffix}.duration_bins_${suffix}.buckets`,
    aggrQuery: { size: 0, aggs: { [`filtered_data_${suffix}`]: { filter, aggs: { [`duration_bins_${suffix}`]: { range: { field: a.custom.rangeField.trim(), keyed: false, ranges } } } } } },
  });
  const second = { bool: { filter: [...clausesOf(base), traitClause(a, c.src)] } };
  return {
    queries: [side('course', base), side('assessment', second)],
    cfg: {
      chartType: 'histogramcomparison', isDecimal: false, aggregationPaths: ['duration_bins_course', 'duration_bins_assessment'],
      filterAggNames: ['filtered_data_course', 'filtered_data_assessment'],
      metricNames: [a.custom.firstBar.trim() || 'Course', a.custom.secondBar.trim() || a.custom.rateLabel.trim() || 'Assessment'],
    },
    handlerKey: 'histogramcomparison',
  };
}

// shape of competencyenrollmentfunnel: the first step per competency, then each level per competency
function buildLevelFunnel(c: RecipeContext): RecipeResult {
  const compName = terms('competencyName.keyword', 1);
  const first: QueryObject = {
    ...envelope(c.srcId, c.a), requestQueryMap: { competencyId: 'competencyId' }, dateRefField: QUALITY_DATES.enr,
    aggrQuery: { aggs: { registeredByCompetency: { filter: filterFor(c.srcId, c.a), aggs: { registered_count: { cardinality: { field: 'userId' } }, comp_name: compName } } } },
  };
  const levels: EsNode = { ...terms('levels', 10), aggs: { level_count: { cardinality: { field: 'userId' } } } };
  const second: QueryObject = {
    ...envelope(c.srcBId, c.a), requestQueryMap: { competencyId: 'competencyID' }, dateRefField: QUALITY_DATES.pass,
    aggrQuery: { aggs: { levelsByCompetency: { filter: filterFor(c.srcBId, c.a), aggs: { comp_name: compName, by_level: levels } } } },
  };
  const competency = KPI_FILTERS.find((f) => f.id === 'competency')?.def;
  return {
    queries: [first, second],
    cfg: {
      chartType: 'competencyenrollmentfunnel', aggregationPaths: ['registeredByCompetency', 'levelsByCompetency'], headerLabel: 'Stage', valueLabel: 'Count',
      kpiFilters: [{ ...competency, key: 'competencyId' }],
    },
    handlerKey: 'competencyenrollmentfunnel',
  };
}

const BUILDERS: Record<ReadyKind, (c: RecipeContext) => RecipeResult> = {
  dropout: buildDropout, reattempt: buildReattempt, attempts: buildAttempts, facility: buildFacility, histogram: buildHistogram, levelFunnel: buildLevelFunnel, completedFunnel: buildLevelFunnel,
};

export const buildReady = (c: RecipeContext): RecipeResult => BUILDERS[c.a.readyKind](c);
